/**
 * Fetch Saved Users Page UseCase
 *
 * Pages through the locally-stored saved-user records (the
 * `SavedUser_<firebaseId>` keys in SecureStorage), fetches each batch's
 * matching user documents from Firestore via `userService.fetchUsersByIds`,
 * re-orders survivors by the slice (Firestore's `in` query does not honour
 * input order), and computes `hasMore` from the slice bounds against the
 * local snapshot length.
 *
 * Mirrors `fetchSavedContentPageUseCase` 1:1 — the differences are:
 *  - local-list element type (`SavedUserStorage` instead of `SavedContentStorage`)
 *  - batch fetch (`userService.fetchUsersByIds` instead of
 *    `userMediaService.getMediasByPaths`)
 *  - keying field (`firebaseId` instead of `relativePath`)
 *
 * The blocked-user filter is applied INSIDE `userService.fetchUsersByIds`
 * (against the caller-supplied `currentUser`); this use case just forwards
 * `currentUser` through and does not re-filter.
 *
 * Flutter parity (with one deliberate web divergence on page size):
 *  - `lib/presentation/screens/user/saved_users_screen.dart:119-171`
 *    (`_getData`: slice by page-size, fetch, re-order against `valuesToSearch`,
 *    recompute `hasMore = _medias.length < _savedUsers.length`).
 *  - `lib/domain/constants/app_constants.dart:22-23`
 *    (Flutter uses `favoriteUsersListPagination = 9`).
 *  - Web deliberately diverges and uses
 *    `SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES` (= 10) as the
 *    page size. The saved-users page issues a Firestore `where in` query
 *    via `userService.fetchUsersByIds`, so the page size IS the `where in`
 *    cap — using the dedicated constant directly removes a duplicate
 *    concept and lets `fetchUsersByIds` skip its chunking loop.
 *
 * `hasMore` semantics: this uses `end < localItems.length` (parity with
 * `fetchSavedContentPageUseCase.ts:110`). When the slice returns at least
 * one survivor, `end` reaches `localItems.length` exactly when
 * `_medias.length` does, so the two formulations are equivalent in the
 * typical flow.
 *
 * Errors from the service are NOT caught here — the hook layer is
 * responsible for logging via `appLogger.error` (parity with
 * `fetchSavedContentPageUseCase`).
 */

import { userService } from '@/domain/services/user/userService';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { SavedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface FetchSavedUsersPageParams {
  localItems: SavedUserStorage[];
  pageIndex: number;
  pageSize?: number;
  currentUser: UserEntity | null;
}

export interface FetchSavedUsersPageResult {
  users: UserEntity[];
  hasMore: boolean;
}

export class FetchSavedUsersPageUseCase {
  /**
   * Execute one page fetch.
   *
   * Steps (mirroring Flutter `saved_users_screen.dart:_getData`):
   *  1. Slice `localItems` by [pageIndex * pageSize, pageIndex * pageSize + pageSize).
   *  2. Fetch the matching user documents (Firestore `in` query — order is
   *     not guaranteed by Firestore).
   *  3. Re-order the surviving users by iterating `idsInSlice` outer, then
   *     matching `user.firebaseId === id` inner — required for parity with
   *     `saved_users_screen.dart:149-164`, since Firestore `in` queries
   *     don't honour input order.
   *  4. Compute `hasMore` from the slice bounds against the local list length.
   *
   * @param params.localItems  The locally-stored snapshot of saved-user records.
   * @param params.pageIndex   Zero-based page index.
   * @param params.pageSize    Defaults to
   *                           `SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES`
   *                           (= 10) — deliberate web divergence from Flutter's
   *                           `favoriteUsersListPagination = 9`. See the file
   *                           header for the rationale.
   * @param params.currentUser Redux-supplied current user. Forwarded verbatim
   *                           to `userService.fetchUsersByIds`, which uses it
   *                           for the blocked-user filter. Not consulted here
   *                           directly.
   */
  async execute(
    params: FetchSavedUsersPageParams,
  ): Promise<FetchSavedUsersPageResult> {
    const {
      localItems,
      pageIndex,
      pageSize = SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES,
      currentUser,
    } = params;

    const start = pageIndex * pageSize;
    if (start >= localItems.length) {
      return { users: [], hasMore: false };
    }

    const end = Math.min(start + pageSize, localItems.length);
    const idsInSlice = localItems
      .slice(start, end)
      .map((item) => item.firebaseId);

    if (idsInSlice.length === 0) {
      return { users: [], hasMore: false };
    }

    const rawUsers = await userService.fetchUsersByIds(idsInSlice, currentUser);

    // Re-order surviving users to match `idsInSlice` order — Flutter
    // saved_users_screen.dart:149-164. Firestore `in` does not preserve
    // input order, and `fetchUsersByIds` may also drop blocked rows, so
    // we iterate the requested ids and only push the matches we find.
    const ordered: UserEntity[] = [];
    for (const id of idsInSlice) {
      const match = rawUsers.find((user) => user.firebaseId === id);
      if (match) {
        ordered.push(match);
      }
    }

    const hasMore = end < localItems.length;

    return { users: ordered, hasMore };
  }
}

export const fetchSavedUsersPageUseCase = new FetchSavedUsersPageUseCase();
